import { Avatar, Spinner } from '@/components/ui';
import { useDirectory } from '@/hooks/queries';
import type { DirectoryUser } from '@/types/api';
import { useEffect, useMemo, useState } from 'react';

/**
 * Selector de personas de la plataforma. Lo usan el chat (a quien escribirle) y
 * los proyectos (a quien sumar), asi que recibe a quien excluir y que hacer al
 * elegir, en vez de saber nada de ninguno de los dos.
 */
export function PeoplePicker({
  title,
  description,
  confirmLabel,
  excludeIds = [],
  emptyHint = 'No queda nadie por agregar.',
  pending = false,
  error,
  onPick,
  onClose,
  children,
}: {
  title: string;
  description?: string;
  confirmLabel: string;
  /** Personas que ya estan dentro y no deben ofrecerse. */
  excludeIds?: string[];
  emptyHint?: string;
  pending?: boolean;
  error?: string | null;
  onPick: (user: DirectoryUser) => void;
  onClose: () => void;
  /** Controles extra, como el rol con el que se suma alguien. */
  children?: React.ReactNode;
}) {
  const [search, setSearch] = useState('');
  const { data: people, isLoading } = useDirectory();

  /**
   * A quien se acaba de elegir. La lista de excluidos viene del servidor y
   * tarda un momento en reflejar el alta; sin esto la persona sigue ofreciendose
   * e invita a pulsar dos veces, lo que devuelve un "ya es miembro".
   */
  const [justPicked, setJustPicked] = useState<string[]>([]);

  // Si la operacion falla, vuelve a ofrecerse.
  useEffect(() => {
    if (error) {
      setJustPicked([]);
    }
  }, [error]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // El filtrado es local: el directorio de una empresa cabe de sobra en memoria
  // y asi escribir no dispara una peticion por tecla.
  const results = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (people ?? [])
      .filter((person) => !excludeIds.includes(person.id) && !justPicked.includes(person.id))
      .filter(
        (person) =>
          !term ||
          person.fullName.toLowerCase().includes(term) ||
          person.username.toLowerCase().includes(term),
      );
  }, [people, search, excludeIds, justPicked]);

  return (
    <div
      role="presentation"
      onClick={onClose}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-[rgba(4,4,10,.62)] p-6"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="people-picker-title"
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[520px] w-full max-w-[440px] flex-col overflow-hidden rounded-panel border border-line bg-surface shadow-[0_24px_60px_rgba(0,0,0,.6)]"
      >
        <header className="flex-none border-b border-line-soft bg-sunken px-5 py-4">
          <h2 id="people-picker-title" className="font-display text-base font-semibold">
            {title}
          </h2>
          {description && <p className="mt-0.5 text-[12.5px] text-subtle">{description}</p>}
        </header>

        <div className="flex-none px-5 pt-4">
          {children}
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            placeholder="Buscar por nombre o usuario"
            aria-label="Buscar personas"
            className="mt-3 w-full rounded-control border border-line-strong bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-brand"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-3 py-3">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : results.length === 0 ? (
            <p className="px-2 py-8 text-center text-[13px] text-faint">
              {search.trim() ? 'Nadie coincide con esa busqueda.' : emptyHint}
            </p>
          ) : (
            <ul className="flex flex-col gap-0.5">
              {results.map((person) => (
                <li key={person.id}>
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => {
                      setJustPicked((current) => [...current, person.id]);
                      onPick(person);
                    }}
                    className="flex w-full items-center gap-2.5 rounded-control px-2 py-2 text-left transition-colors hover:bg-canvas disabled:opacity-50"
                  >
                    <Avatar user={person} size={30} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-medium">
                        {person.fullName}
                      </span>
                      <span className="block truncate text-[11.5px] text-faint">
                        {person.jobTitle ?? `@${person.username}`}
                      </span>
                    </span>
                    <span className="flex-none text-[12px] font-semibold text-brand-deep">
                      {confirmLabel}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {error && (
          <p role="alert" className="flex-none px-5 pb-2 text-[13px] text-danger-text">
            {error}
          </p>
        )}

        <footer className="flex flex-none justify-end border-t border-line-soft bg-sunken px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-control px-3 py-2 text-[13px] font-medium text-subtle transition-colors hover:text-ink"
          >
            Cerrar
          </button>
        </footer>
      </div>
    </div>
  );
}
